import { Doc } from '@dadohr/shared/domainTypes/document';
import { MERGE_CUSTOM_ID } from '@dadohr/shared/domainTypes/integrations/merge';
import {
  CustomUserField,
  toCustomFieldKey
} from '@dadohr/shared/domainTypes/org';
import { DiffSet, IUser } from '@dadohr/shared/domainTypes/user';
import styled from '@emotion/styled';
import { Tab, Tabs } from '@material-ui/core';
import { get } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { ColumnSelector } from '../../../../../components/ColumnSelector';
import { RowsRendererScrollContainer } from '../../../../../components/GroupableList';
import { TableSearch } from '../../../../../components/Table';
import { UserTable } from '../../../../../components/UserTable';
import {
  DETAILED_NAME_COLUMNS,
  UserTableColumn,
  useUserTableColumnsAndSorters
} from '../../../../../components/UserTable/columns';
import { UserDiffTable } from '../../../../../components/UserTable/DiffTable';
import { UserIssuesTable } from '../../../../../components/UserTable/IssuesTable';
import { useUnsafeTranslation } from '../../../../../i18next';
import { Centered } from '../../../../../layout/Centered';
import { FlexContainer } from '../../../../../layout/Flex';
import {
  matchUserBySearchTerm,
  toSearchTermRegexp
} from '../../../../../services/user';

const TabContainer = styled('div')((p) => ({
  marginTop: p.theme.spacing(2),
  marginBottom: p.theme.spacing(4),
  background: p.theme.palette.background.paper
}));

const TabPanel: React.FC<{ active: boolean }> = ({ active, children }) => {
  return (
    <div role="tabpanel" hidden={!active}>
      {active && <TabContainer>{children}</TabContainer>}
    </div>
  );
};

const hasValue = (x: any) => x !== null && x !== undefined && x !== '';

const withIgnoredEmptyColumns = (
  columns: UserTableColumn[],
  users: Doc<IUser>[]
) => {
  return columns.filter((c) => {
    if (c.key === toCustomFieldKey(MERGE_CUSTOM_ID)) {
      return false;
    }
    // always show name and emails
    if (c.key === 'name' || c.key === 'email' || c.key === 'personalEmail') {
      return true;
    }
    if (DETAILED_NAME_COLUMNS.includes(c.key)) {
      return false;
    }
    return !!users.find((u) => hasValue(get(u.data, c.key)));
  });
};

const withIgnoredUnchangedColumns = (
  columns: UserTableColumn[],
  requiredNameCols: Set<string>,
  changes: DiffSet['changed']
) => {
  const hasAnyUpdatedValue = (key: string) =>
    !!changes.find((u) => !!get(u.changes, key));
  return columns.filter((c) => {
    if (requiredNameCols.has(c.key)) {
      return true;
    }

    // other custom ids by import sources could also be hidden here?
    if (c.key === toCustomFieldKey(MERGE_CUSTOM_ID)) {
      return false;
    }
    if (c.key === 'email') {
      return true;
    }
    if (
      c.key === 'name' &&
      DETAILED_NAME_COLUMNS.some((c) => requiredNameCols.has(c))
    ) {
      return false;
    }
    if (
      DETAILED_NAME_COLUMNS.includes(c.key) &&
      !DETAILED_NAME_COLUMNS.some((c) => requiredNameCols.has(c))
    ) {
      return false;
    }

    return hasAnyUpdatedValue(c.key);
  });
};

const findRequiredNameColumnsForChanges = (d: DiffSet): Set<string> => {
  // See https://app.shortcut.com/emplexp/story/4231/import-details-page-be-smarter-about-which-columns-we-show-in-changed-users-tab
  // for what we're doing here
  const hasAnyNameChange = !!d.changed.find((x) => {
    return DETAILED_NAME_COLUMNS.some((c) => !!x.changes[c]);
  });

  if (hasAnyNameChange) {
    return new Set(DETAILED_NAME_COLUMNS);
  }

  return new Set(['name']);
};

const TABS = {
  ADDED: 0,
  CHANGED: 1,
  REMOVED: 2,
  ISSUES: 3
};

export const DiffSetRenderer = ({
  d,
  customFields,
  dateFormat
}: {
  d: DiffSet;
  customFields: CustomUserField[];
  dateFormat: string;
}) => {
  const { t } = useUnsafeTranslation();
  const { columns: allColumns, sorters } = useUserTableColumnsAndSorters(
    customFields
  );

  const [q, setQ] = useState('');

  console.log('diffset', d);

  const [tab, setTab] = useState(
    d.added.length
      ? TABS.ADDED
      : d.changed.length
      ? TABS.CHANGED
      : d.removed.length
      ? TABS.REMOVED
      : d.issues.length
      ? TABS.ISSUES
      : TABS.ADDED
  );

  const requiredNameColumns = useMemo(
    () =>
      tab === TABS.CHANGED
        ? findRequiredNameColumnsForChanges(d)
        : new Set(['name']),
    [d, tab]
  );

  console.log('REQUI', requiredNameColumns);

  const columns = useMemo(() => {
    if (requiredNameColumns.has('name')) {
      const otherNameColumns = new Set(DETAILED_NAME_COLUMNS);
      return allColumns.filter((c) => !otherNameColumns.has(c.key));
    } else {
      return allColumns.filter((c) => c.key !== 'name');
    }
  }, [requiredNameColumns, allColumns]);

  const getInitialSelectedColumns = (selectedTab: number) =>
    selectedTab === TABS.ADDED
      ? withIgnoredEmptyColumns(columns, d.added)
      : selectedTab === TABS.REMOVED
      ? withIgnoredEmptyColumns(columns, d.removed)
      : selectedTab === TABS.CHANGED
      ? withIgnoredUnchangedColumns(columns, requiredNameColumns, d.changed)
      : columns;

  const [selectedColumns, setSelectedColumns] = useState(
    getInitialSelectedColumns(tab)
  );

  useEffect(() => {
    setSelectedColumns(getInitialSelectedColumns(tab));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const hasContent = !!(
    d.added.length +
    d.changed.length +
    d.removed.length +
    d.issues.length
  );

  const allUpdatedUsers = useMemo(() => {
    return [...d.added, ...d.changed.map((u) => u.aft)];
  }, [d]);

  const filteredD = useMemo<DiffSet>(() => {
    if (!q) {
      return d;
    }
    const re = toSearchTermRegexp(q);
    const matchUser = (u: Doc<IUser>) => matchUserBySearchTerm(re, u);
    return {
      ...d,
      added: d.added.filter(matchUser),
      changed: d.changed.filter((c) => matchUser(c.aft)),
      removed: d.removed.filter(matchUser)
    };
  }, [d, q]);

  if (!hasContent) {
    return <Centered height={200}>{t('Nothing changed.')}</Centered>;
  }

  return (
    <>
      <Tabs
        value={tab}
        onChange={(ev, i) => setTab(i)}
        indicatorColor="primary"
        textColor="primary"
        // variant="fullWidth"
      >
        <Tab
          value={TABS.ADDED}
          label={`${t('import.changes.added')} (${d.added.length})`}
          wrapped
          disabled={!d.added.length}
        />
        <Tab
          value={TABS.CHANGED}
          label={`${t('import.changes.changed')} (${d.changed.length})`}
          wrapped
          disabled={!d.changed.length}
        />
        <Tab
          value={TABS.REMOVED}
          label={`${t('import.changes.removed')} (${d.removed.length})`}
          wrapped
          disabled={!d.removed.length}
        />
        <Tab
          value={TABS.ISSUES}
          label={`${t('import.changes.issues')} (${d.issues.length})`}
          wrapped
          disabled={!d.issues.length}
        />
      </Tabs>

      <TabPanel active={tab === TABS.ADDED}>
        <FlexContainer justifyContent={'flex-end'}>
          <TableSearch
            term={q}
            onChange={setQ}
            autoFocus
            placeholder="Search by name"
          />
          <ColumnSelector
            value={selectedColumns}
            onChange={setSelectedColumns}
            columns={columns}
            requiredColumns={requiredNameColumns}
          />
        </FlexContainer>
        <RowsRendererScrollContainer height={700}>
          <UserTable
            ds={filteredD.added}
            columns={selectedColumns}
            sorters={sorters}
            variant="plain"
            additionalUsers={allUpdatedUsers}
            headProps={{ sticky: true, offset: 0 }}
            dateFormat={dateFormat}
          />
        </RowsRendererScrollContainer>
      </TabPanel>
      <TabPanel active={tab === TABS.CHANGED}>
        <FlexContainer justifyContent={'flex-end'}>
          <TableSearch
            term={q}
            onChange={setQ}
            autoFocus
            placeholder="Search by name"
          />
          <ColumnSelector
            value={selectedColumns}
            onChange={setSelectedColumns}
            columns={columns}
            requiredColumns={requiredNameColumns}
          />
        </FlexContainer>
        <RowsRendererScrollContainer height={700}>
          <UserDiffTable
            ds={filteredD.changed}
            columns={selectedColumns}
            sorters={sorters}
            variant="plain"
            additionalUsers={allUpdatedUsers}
            dateFormat={dateFormat}
          />
        </RowsRendererScrollContainer>
      </TabPanel>
      <TabPanel active={tab === TABS.REMOVED}>
        <FlexContainer justifyContent={'flex-end'}>
          <TableSearch
            term={q}
            onChange={setQ}
            autoFocus
            placeholder="Search by name"
          />
          <ColumnSelector
            value={selectedColumns}
            onChange={setSelectedColumns}
            columns={columns}
            requiredColumns={requiredNameColumns}
          />
        </FlexContainer>
        <RowsRendererScrollContainer height={700}>
          <UserTable
            ds={filteredD.removed}
            columns={selectedColumns}
            sorters={sorters}
            variant="plain"
            additionalUsers={allUpdatedUsers}
            headProps={{ sticky: true, offset: 0 }}
            dateFormat={dateFormat}
          />
        </RowsRendererScrollContainer>
      </TabPanel>

      <TabPanel active={tab === TABS.ISSUES}>
        <RowsRendererScrollContainer height={700}>
          <UserIssuesTable ds={d.issues} variant="plain" />
        </RowsRendererScrollContainer>
      </TabPanel>
    </>
  );
};
